import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Reveal from "@/components/Reveal";

const CASE_STUDIES = [
  {
    title: "Vi — Interactive Touch Kiosks",
    tag: "30+ Locations · Pan-India Rollout",
    img: "https://customer-assets-lxgj4vgw.emergentagent.net/job_led-web-platform/artifacts/fb7dd414860c7581_6fd1f813-a6b1-432e-9097-52c2db5f6d57.png",
  },
  {
    title: "Vi — Outdoor LED Facade",
    tag: "50+ Stores · P4 Outdoor LED",
    img: "https://customer-assets-lxgj4vgw.emergentagent.net/job_led-web-platform/artifacts/6b6f25779d52bca6_0901a8d8-834a-4302-80fa-fce3f51283df.png",
  },
  {
    title: "Hyundai — Showroom Touch Kiosks",
    tag: "15+ Cities · Immersive Showroom Experience",
    img: "https://customer-assets-lxgj4vgw.emergentagent.net/job_led-web-platform/artifacts/08cbfa1ee7a92c7e_e2404321-06da-44db-b9ee-d1ce73c9e159.png",
  },
];

const AUTOPLAY_MS = 7000;

const CaseStudies = () => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = CASE_STUDIES.length;
  const timerRef = useRef(null);

  const next = () => setIdx((i) => (i + 1) % total);
  const prev = () => setIdx((i) => (i - 1 + total) % total);
  const go = (i) => setIdx(i);

  useEffect(() => {
    if (paused) return;
    timerRef.current = setInterval(next, AUTOPLAY_MS);
    return () => clearInterval(timerRef.current);
  }, [paused]);

  return (
    <section
      id="case-studies"
      data-testid="case-studies-section"
      className="relative py-24 lg:py-32 bg-[#0B1220] text-white overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-70 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(900px circle at 15% 15%, rgba(0,67,156,0.28), transparent 55%), radial-gradient(700px circle at 90% 85%, rgba(168,137,88,0.10), transparent 60%)",
        }}
        aria-hidden
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal className="max-w-3xl mb-14 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <div className="text-[12px] uppercase tracking-[0.18em] font-semibold text-[#A88958] mb-3">
              Case Studies
            </div>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Impact at
              <br />
              <span className="text-[#A88958]">scale.</span>
            </h2>
            <p className="mt-6 text-white/65 text-base lg:text-lg leading-relaxed max-w-xl">
              Multi-city, multi-store programs engineered end-to-end by Smart Digi — from
              industrial design to pan-India rollout, calibration, and ongoing support.
            </p>
          </div>
        </Reveal>

        <div
          data-testid="case-studies-carousel"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#0F172A] shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6)]"
        >
          {/* Sliding track */}
          <div
            className="flex transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
            style={{ transform: `translateX(-${idx * 100}%)` }}
          >
            {CASE_STUDIES.map((cs, i) => (
              <div
                key={cs.title}
                data-testid={`case-study-slide-${i}`}
                className="relative shrink-0 w-full"
              >
                <img
                  src={cs.img}
                  alt={cs.title}
                  loading={i === 0 ? "eager" : "lazy"}
                  decoding="async"
                  className="showcase-img block w-full h-auto"
                />
              </div>
            ))}
          </div>

          {/* Prev / Next arrows */}
          <button
            type="button"
            onClick={prev}
            aria-label="Previous case study"
            data-testid="case-prev"
            className="absolute top-1/2 left-3 lg:left-5 -translate-y-1/2 w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-black/55 backdrop-blur-sm border border-white/15 text-white hover:bg-[#00439C] hover:border-[#00439C] transition flex items-center justify-center"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="Next case study"
            data-testid="case-next"
            className="absolute top-1/2 right-3 lg:right-5 -translate-y-1/2 w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-black/55 backdrop-blur-sm border border-white/15 text-white hover:bg-[#00439C] hover:border-[#00439C] transition flex items-center justify-center"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Dots + counter */}
        <div className="mt-6 flex items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            {CASE_STUDIES.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => go(i)}
                aria-label={`Go to case study ${i + 1}`}
                data-testid={`case-dot-${i}`}
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  idx === i ? "w-10 bg-white" : "w-4 bg-white/30 hover:bg-white/60"
                }`}
              />
            ))}
          </div>
          <div className="text-[12px] uppercase tracking-[0.18em] font-semibold text-white/55">
            {String(idx + 1).padStart(2, "0")} <span className="text-white/25 mx-1">/</span>{" "}
            {String(total).padStart(2, "0")}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
